import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 20_000 });

// Normalizzatore centralizzato per collezioni API.
// Restituisce SEMPRE un array indipendentemente dalla forma della risposta.
//   {total, items:[...]} -> [...]
//   {results:[...]}     -> [...]
//   [...]               -> [...]
//   altro/null           -> []
export function toArray(res) {
  const d = res?.data ?? res;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.items)) return d.items;
  if (d && Array.isArray(d.results)) return d.results;
  return [];
}

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("ma_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Mappa errori tecnici a messaggi utente in italiano.
// Distingue rete/timeout/backend-down (nessuna response) dalle
// risposte HTTP effettive (401/403/404/409/422/429/5xx).
function humanMessage(err) {
  // Errori senza response = backend non raggiungibile o timeout
  if (!err?.response) {
    if (err?.code === "ERR_NETWORK" || err?.message === "Network Error") {
      return "Server non raggiungibile. Verifica la connessione.";
    }
    if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
      return "Il server sta impiegando troppo tempo a rispondere. Riprova.";
    }
    return "Impossibile contattare il server.";
  }

  const s = err.response.status;
  const detail = err.response.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;

  if (s === 400) return "Richiesta non valida. Verifica i dati inseriti.";
  if (s === 401) return "Sessione scaduta. Effettua di nuovo l'accesso.";
  if (s === 403) return "Non hai i permessi per questa operazione.";
  if (s === 404) return "Risorsa non trovata.";
  if (s === 409) return "Operazione in conflitto (dato già esistente).";
  if (s === 422) return "Dati inseriti non validi. Controlla i campi obbligatori.";
  if (s === 429) return "Troppi tentativi. Riprova più tardi.";
  if (s === 502 || s === 503 || s === 504) return "Servizio temporaneamente non disponibile.";
  if (s >= 500) return "Errore del server. Riprova o contatta l'amministratore.";
  return "Operazione non riuscita.";
}

// Marker: axios error già gestito (toast già mostrato).
// Impedisce ai global handler di duplicare il messaggio.
function markHandled(err) {
  try { if (err && typeof err === "object") err.__maHandled = true; } catch {}
  return err;
}

api.interceptors.response.use(
  (r) => r,
  (err) => {
    // Log tecnico dettagliato solo in console
    try {
      console.warn(
        "[api]",
        err?.config?.method?.toUpperCase() || "?",
        err?.config?.url || "?",
        err?.response?.status || err?.code || "no-response",
        err?.response?.data ?? err?.message,
      );
    } catch {}

    if (err?.response?.status === 401) {
      localStorage.removeItem("ma_token");
      if (!window.location.pathname.startsWith("/login") &&
          !window.location.pathname.startsWith("/setup")) {
        window.location.href = "/login";
      }
    } else if (err?.config?.silent !== true) {
      try { toast.error(humanMessage(err)); } catch {}
    }
    markHandled(err);
    return Promise.reject(err);
  },
);

// Helper per capire dal chiamante se un errore è già stato notificato
export function isHandledApiError(err) {
  return !!(err && err.__maHandled);
}

export default api;
